import { css, html, LitElement, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import type {
    Commerce,
    CommerceAddress,
    CommerceCartPurchaseType,
    CommerceCheckout,
    CommerceProduct,
} from "@teqtank/commerce-sdk";
import "../payment/teqtank-payment.js";

@customElement("teqtank-quick-buy")
export class TeqtankQuickBuy extends LitElement {
    @property({ attribute: false }) public accessor commerce: Commerce | undefined;
    @property({ attribute: false }) public accessor product: CommerceProduct | undefined;
    @property({ attribute: "variant-id" }) public accessor variantId = "";
    @property({ type: Number }) public accessor quantity = 1;
    @property({ attribute: "purchase-type" })
    public accessor purchaseType: CommerceCartPurchaseType = "one-time";
    @property({ attribute: "mock-payment", type: Boolean }) public accessor mockPayment = false;
    @state() private accessor checkout: CommerceCheckout | undefined;
    @state() private accessor loading = true;
    @state() private accessor errorMessage = "";

    public static override styles = css`
        :host {
            position: fixed;
            inset: 0;
            z-index: 10000;
            display: grid;
            place-items: center;
            padding: 20px;
            font-family: var(--teqtank-font-body, Inter, ui-sans-serif, system-ui, sans-serif);
            color: var(--teqtank-color-text, #18221e);
        }
        .backdrop {
            position: absolute;
            inset: 0;
            background: rgba(14, 20, 17, 0.56);
            backdrop-filter: blur(12px);
            animation: fade-in 180ms ease-out both;
        }
        .modal {
            position: relative;
            width: min(620px, 100%);
            max-height: min(88vh, 820px);
            overflow: auto;
            border: 1px solid var(--teqtank-color-border, rgba(24, 34, 30, 0.12));
            border-radius: 24px;
            background: var(--teqtank-color-surface, #fff);
            box-shadow: 0 32px 90px rgba(0, 0, 0, 0.28);
            animation: rise-in 240ms cubic-bezier(0.2, 0.8, 0.2, 1) both;
        }
        header {
            position: sticky;
            top: 0;
            z-index: 2;
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 20px 22px;
            border-bottom: 1px solid var(--teqtank-color-border, rgba(24, 34, 30, 0.12));
            background: color-mix(in srgb, var(--teqtank-color-surface, #fff) 92%, transparent);
            backdrop-filter: blur(14px);
        }
        .eyebrow {
            margin: 0 0 4px;
            color: var(--teqtank-color-accent, #8d5c3f);
            font-size: 10px;
            font-weight: 900;
            letter-spacing: 0.14em;
        }
        h2 {
            margin: 0;
            font: 500 26px var(--teqtank-font-heading, Georgia, serif);
        }
        .close {
            width: 38px;
            height: 38px;
            border: 1px solid var(--teqtank-color-border, #ddd);
            border-radius: 999px;
            background: transparent;
            font-size: 20px;
            cursor: pointer;
        }
        .body {
            display: grid;
            gap: 22px;
            padding: 22px;
        }
        .product {
            display: grid;
            grid-template-columns: 68px 1fr auto;
            gap: 14px;
            align-items: center;
        }
        .product img {
            width: 68px;
            height: 68px;
            object-fit: var(--teqtank-product-image-fit, contain);
            border-radius: 14px;
            background: var(--teqtank-color-surface-soft, #f3f0ea);
        }
        .product strong,
        .product small {
            display: block;
        }
        .product small,
        .muted {
            color: var(--teqtank-color-text-muted, #69736d);
        }
        .section {
            display: grid;
            gap: 10px;
        }
        .section-title {
            display: flex;
            justify-content: space-between;
            align-items: baseline;
        }
        .section-title strong {
            font-size: 13px;
        }
        .choices {
            display: grid;
            gap: 8px;
        }
        .choice {
            display: grid;
            grid-template-columns: auto 1fr auto;
            gap: 10px;
            align-items: center;
            padding: 12px 13px;
            border: 1px solid var(--teqtank-color-border, #ddd);
            border-radius: 14px;
            cursor: pointer;
            transition:
                transform 160ms ease,
                border-color 160ms ease,
                background 160ms ease;
        }
        .choice:hover {
            transform: translateY(-1px);
            border-color: var(--teqtank-color-accent, #8d5c3f);
        }
        .choice:has(input:checked) {
            border-color: var(--teqtank-color-accent, #8d5c3f);
            background: color-mix(in srgb, var(--teqtank-color-accent, #8d5c3f) 7%, white);
        }
        .choice span {
            display: grid;
            gap: 2px;
        }
        .choice small {
            color: var(--teqtank-color-text-muted, #69736d);
        }
        .total {
            display: flex;
            align-items: end;
            justify-content: space-between;
            padding-top: 16px;
            border-top: 1px solid var(--teqtank-color-border, #ddd);
        }
        .total strong {
            font-size: 24px;
        }
        .error {
            padding: 12px 14px;
            border-radius: 12px;
            background: #fff0f0;
            color: #8b2424;
        }
        .loading {
            position: relative;
            padding: 42px;
            border-radius: 22px;
            background: white;
        }
        @keyframes fade-in {
            from {
                opacity: 0;
            }
        }
        @keyframes rise-in {
            from {
                opacity: 0;
                transform: translateY(18px) scale(0.985);
            }
        }
        @media (prefers-reduced-motion: reduce) {
            .backdrop,
            .modal {
                animation: none;
            }
            .choice {
                transition: none;
            }
        }
        @media (max-width: 560px) {
            :host {
                padding: 0;
                place-items: end center;
            }
            .modal {
                width: 100%;
                max-height: 94vh;
                border-radius: 24px 24px 0 0;
            }
        }
    `;

    protected override firstUpdated(): void {
        void this.begin();
    }

    protected override render() {
        return html`
            <div class="backdrop" @click=${this.close}></div>
            ${this.loading ? html`<div class="loading">Preparing Quick Buy…</div>` : this.checkout ? this.renderCheckout(this.checkout) : html`<div class="loading">${this.errorMessage || "Quick Buy is unavailable."}</div>`}
        `;
    }

    private renderCheckout(checkout: CommerceCheckout) {
        const customer = this.commerce?.customer.customer;
        const product = this.product;
        if (!customer || !product) return nothing;
        const image = product.primaryImage ?? product.images[0] ?? null;
        return html`
            <section class="modal" role="dialog" aria-modal="true" aria-label="Quick Buy">
                <header>
                    <div>
                        <p class="eyebrow">QUICK BUY</p>
                        <h2>Ready in one click.</h2>
                    </div>
                    <button
                        class="close"
                        type="button"
                        aria-label="Close Quick Buy"
                        @click=${this.close}
                    >
                        ×
                    </button>
                </header>
                <div class="body">
                    ${this.errorMessage ? html`<div class="error" role="alert">${this.errorMessage}</div>` : nothing}
                    <div class="product">
                        ${image ? html`<img src=${image.url} alt=${image.alternativeText} />` : html`<div></div>`}
                        <div>
                            <strong>${product.name}</strong
                            ><small
                                >${this.quantity} ×
                                ${this.purchaseType === "recurring" ? "Subscription" : "One-time purchase"}</small
                            >
                        </div>
                        <strong
                            >${checkout.items[0]?.lineTotal.formatted ?? checkout.totals.subtotal.formatted}</strong
                        >
                    </div>

                    <div class="section">
                        <div class="section-title">
                            <strong>Deliver to</strong
                            ><span class="muted">${customer.displayName}</span>
                        </div>
                        <div class="choices">
                            ${customer.addresses
                                .filter((item) => item.type !== "billing")
                                .map(
                                    (item) => html`
                                        <label class="choice">
                                            <input
                                                type="radio"
                                                name="quickAddress"
                                                ?checked=${this.sameAddress(item.address, checkout.shippingAddress)}
                                                @change=${() => this.selectAddress(item.address)}
                                            />
                                            <span
                                                ><strong>${item.label}</strong
                                                ><small
                                                    >${item.address.addressLine1},
                                                    ${item.address.city}
                                                    ${item.address.postalCode}</small
                                                ></span
                                            >
                                        </label>
                                    `,
                                )}
                        </div>
                    </div>

                    <div class="section">
                        <div class="section-title"><strong>Shipping</strong></div>
                        <div class="choices">
                            ${checkout.shippingMethods.map(
                                (method) => html`
                                    <label class="choice">
                                        <input
                                            type="radio"
                                            name="quickShipping"
                                            ?checked=${method.id === checkout.selectedShippingMethodId}
                                            @change=${() => this.selectShipping(method.id)}
                                        />
                                        <span
                                            ><strong>${method.name}</strong
                                            ><small>${method.estimatedDelivery}</small></span
                                        >
                                        <strong>${method.price.formatted}</strong>
                                    </label>
                                `,
                            )}
                        </div>
                    </div>

                    <div class="section">
                        <div class="section-title"><strong>Pay with</strong></div>
                        <div class="choices">
                            ${(customer.savedPaymentMethods ?? []).map(
                                (method) => html`
                                    <label class="choice">
                                        <input
                                            type="radio"
                                            name="quickPayment"
                                            ?checked=${method.id === checkout.selectedSavedPaymentMethodId}
                                            @change=${() => this.selectPayment(method.id)}
                                        />
                                        <span
                                            ><strong>${method.displayName}</strong
                                            ><small
                                                >${method.brand ?? method.type}${method.expiryMonth && method.expiryYear ? ` · ${String(method.expiryMonth).padStart(2, "0")}/${String(method.expiryYear).slice(-2)}` : ""}</small
                                            ></span
                                        >
                                    </label>
                                `,
                            )}
                        </div>
                    </div>

                    <div class="total">
                        <span
                            ><small class="muted">Calculated total</small><br /><strong
                                >${checkout.totals.total.formatted}</strong
                            ></span
                        >
                    </div>
                    <teqtank-payment
                        .commerce=${this.commerce}
                        .checkout=${checkout}
                        .mockMode=${this.mockPayment}
                        .hideFrame=${true}
                        @teqtank-payment-complete=${this.complete}
                        @teqtank-payment-failed=${this.failed}
                    ></teqtank-payment>
                </div>
            </section>
        `;
    }

    private async begin(): Promise<void> {
        if (!this.commerce || !this.product || !this.commerce.customer.isAuthenticated) {
            this.loading = false;
            this.errorMessage = "Sign in to use Quick Buy.";
            return;
        }
        try {
            const variant =
                this.product.variants.find((candidate) => candidate.id === this.variantId) ??
                this.product.variants.find(
                    (candidate) => candidate.id === this.product?.defaultVariantId,
                );
            const offer = variant?.offers.find(
                (candidate) => candidate.purchaseType === this.purchaseType,
            );
            const frequency = offer?.frequency?.options.find(
                (candidate) => candidate.id === offer.frequency?.defaultOptionId,
            );
            this.checkout = await this.commerce.checkout.beginQuickBuy({
                productId: this.product.id,
                ...(this.variantId ? { variantId: this.variantId } : {}),
                quantity: this.quantity,
                purchaseType: this.purchaseType,
                ...(frequency === undefined
                    ? {}
                    : { frequencyId: frequency.id, frequencyDays: frequency.days }),
            });
            this.checkout = await this.commerce.checkout.calculate();
        } catch (error) {
            this.errorMessage =
                error instanceof Error ? error.message : "Quick Buy could not be prepared.";
        } finally {
            this.loading = false;
        }
    }

    private async selectAddress(address: CommerceAddress): Promise<void> {
        if (!this.commerce) return;
        this.checkout = await this.commerce.checkout.updateShippingAddress(address);
        this.checkout = await this.commerce.checkout.calculate();
    }

    private async selectShipping(id: string): Promise<void> {
        if (!this.commerce) return;
        this.checkout = await this.commerce.checkout.selectShippingMethod(id);
        this.checkout = await this.commerce.checkout.calculate();
    }

    private async selectPayment(id: string): Promise<void> {
        if (!this.commerce) return;
        this.checkout = await this.commerce.checkout.selectSavedPaymentMethod(id);
    }

    private sameAddress(left: CommerceAddress, right: CommerceAddress | null): boolean {
        return (
            right !== null &&
            left.addressLine1 === right.addressLine1 &&
            left.postalCode === right.postalCode &&
            left.countryCode === right.countryCode
        );
    }

    private readonly complete = (event: CustomEvent): void => {
        this.dispatchEvent(
            new CustomEvent("teqtank-quick-buy-complete", {
                detail: event.detail,
                bubbles: true,
                composed: true,
            }),
        );
        this.close();
    };

    private readonly failed = (event: CustomEvent<{ error: unknown }>): void => {
        this.errorMessage =
            event.detail.error instanceof Error
                ? event.detail.error.message
                : "Quick Buy payment failed.";
    };

    private readonly close = (): void => {
        this.dispatchEvent(
            new CustomEvent("teqtank-quick-buy-close", { bubbles: true, composed: true }),
        );
    };
}

declare global {
    interface HTMLElementTagNameMap {
        "teqtank-quick-buy": TeqtankQuickBuy;
    }
}
